import type { ProviderId, TileType } from '../schema/index.js';
import type { DataState } from './states.js';

/**
 * ============================================================================
 * H1 — CACHE ENDPOINT CONTRACT  (ADR-001 §3, §8 H1; ADR-002 §5)
 * ============================================================================
 *
 * The ONE mandatory server component in Phase 1 and the D-007 cost mechanism.
 * The client never calls OpenWeather / a quotes API / a news API directly — it
 * calls OUR proxy, which holds all upstream keys server-side, dedupes by cache
 * key, and returns normalized JSON.
 *
 *   Endpoint:  GET /api/tile?source=<provider>&type=<tileType>&<validated params>
 *
 * This module is the FROZEN contract. platform-dev builds the real Worker against
 * it; tile-dev builds adapters against the mock cache (src/mocks) that implements
 * it. Neither side may change these types without a tech-lead ADR amendment.
 *
 * ---------------------------------------------------------------------------
 * REQUEST
 * ---------------------------------------------------------------------------
 * Query parameters (all validated server-side before ANY upstream fetch —
 * ADR-002 §4 "Cache proxy request" boundary; never fetch on invalid input):
 *   - `source`  : a ProviderId in the server allowlist (SSRF firewall #1).
 *   - `type`    : the TileType, selects the param schema + TTL.
 *   - <params>  : the tile type's params (ADR-002 §3.2), flattened into the
 *                 query string; the server re-validates them with the SAME Zod
 *                 schema the client used (one shared module — ADR-002 §4).
 *
 * The server derives the cache key with `deriveCacheKey({type, provider, params})`
 * (src/schema) so the client and server agree on identity byte-for-byte.
 *
 * ---------------------------------------------------------------------------
 * RESPONSE
 * ---------------------------------------------------------------------------
 * Always HTTP 200 with a JSON envelope EXCEPT for malformed requests (4xx) — a
 * degraded upstream is a 200 with an `error`/`stale` envelope so a single bad
 * provider never throws in the client; the tile degrades, the board survives
 * (ADR-002 §4). The envelope's `state` field (DataState) drives the tile UI.
 *
 * The `data` payload is the adapter's NORMALIZED output (H2 `Data`) — provider
 * quirks already ironed out server-side. The client never sees a raw provider
 * payload, a URL, or a key.
 * ============================================================================
 */

/** HTTP status codes this endpoint emits. 200 for all envelopes; 4xx for bad input. */
export const CACHE_HTTP = {
  OK: 200,
  BAD_REQUEST: 400, // failed param/allowlist validation — never fetched
  NOT_FOUND: 404, // unknown route
} as const;

/** Stable machine-readable error codes in the error envelope (never leak internals). */
export const CACHE_ERROR_CODES = [
  'invalid_request', // params/allowlist/type failed server validation (maps to 400)
  'upstream_error', // provider returned a non-2xx or unparseable payload
  'upstream_timeout', // provider did not respond in time
  'rate_limited', // provider (or our proxy) 429'd
  'not_configured', // provider key/config missing server-side
] as const;
export type CacheErrorCode = (typeof CACHE_ERROR_CODES)[number];

/**
 * The wire query-parameter NAME for the provider. ADR-001 specifies the endpoint
 * as `GET /api/tile?source=<provider>` — so on the URL the provider travels under
 * the key `source`. In TypeScript the field is named `provider` (see below) to
 * stay consistent with `deriveCacheKey({provider})`, the adapter's `provider`,
 * and the tile config's `source.provider`; this constant is the single place that
 * records the wire/field mapping so client and server serialize identically.
 */
export const PROVIDER_QUERY_PARAM = 'source' as const;

/**
 * The request descriptor (what the client intends). Serialized into the query
 * string by the client (`provider` → `?source=…`, `type` → `?type=…`, params
 * flattened) and re-parsed + re-validated by the server. `params` is the type's
 * validated params object (ADR-002 §3.2).
 *
 * NOTE: the TS field is `provider` while the URL key is `source` (ADR-001 wire
 * name) — see `PROVIDER_QUERY_PARAM`. This keeps the field name aligned with the
 * rest of the codebase (`deriveCacheKey`, adapter `provider`, `source.provider`).
 */
export interface CacheRequest<Params = Readonly<Record<string, unknown>>> {
  readonly provider: ProviderId;
  readonly type: TileType;
  readonly params: Readonly<Params>;
}

/** Metadata every envelope carries, regardless of success/failure. */
export interface CacheMeta {
  /** The canonical cache key this response was served under (deriveCacheKey). */
  readonly cacheKey: string;
  /** Whether this value came from cache (`true`) or a fresh upstream fetch. */
  readonly cached: boolean;
  /** Epoch ms when the underlying data was fetched upstream (freshness anchor). */
  readonly fetchedAt: number;
  /** Epoch ms after which the cached value is considered stale (fetchedAt + ttl*1000). */
  readonly expiresAt: number;
  /** The TTL (seconds) applied to this tile type (from TTL_BY_TILE_TYPE). */
  readonly ttlSec: number;
}

/** Success/stale envelope — carries normalized data. `state` is `ok` or `stale`. */
export interface CacheDataEnvelope<Data = unknown> {
  readonly state: Extract<DataState, 'ok' | 'stale'>;
  readonly data: Readonly<Data>;
  readonly meta: CacheMeta;
  /** Present only when `state === 'error'`; absent here. */
  readonly error?: undefined;
}

/**
 * Error envelope — the fetch failed. Carries `lastKnown` data when a previously
 * cached value exists (so the tile can show a faded last value + "Unavailable" —
 * glance-spec §6.2) and never carries provider internals.
 */
export interface CacheErrorEnvelope<Data = unknown> {
  readonly state: Extract<DataState, 'error' | 'rateLimited'>;
  readonly error: {
    readonly code: CacheErrorCode;
    /** Safe, user-facing-ish summary. NEVER an upstream URL, key, or stack. */
    readonly message: string;
    /** Optional epoch ms hint for when to retry (set on rate_limited). */
    readonly retryAtMs?: number;
  };
  /** Last successfully cached data, if any (glance-spec §6.2 faded last value). */
  readonly lastKnown?: Readonly<Data>;
  readonly meta: CacheMeta;
  /** Absent on the error envelope (present only on the data envelope). */
  readonly data?: undefined;
}

/** The full response union the client parses. Discriminated on `state`. */
export type CacheResponse<Data = unknown> =
  | CacheDataEnvelope<Data>
  | CacheErrorEnvelope<Data>;

/** Narrowing helpers (pure). */
export function isCacheError<Data>(
  res: CacheResponse<Data>,
): res is CacheErrorEnvelope<Data> {
  return res.state === 'error' || res.state === 'rateLimited';
}
export function isCacheData<Data>(
  res: CacheResponse<Data>,
): res is CacheDataEnvelope<Data> {
  return res.state === 'ok' || res.state === 'stale';
}
